import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DATA } from "../../data/products-data";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { AnimatePresence, motion } from "framer-motion";
import {
  containerVariants,
  itemVariants,
  photoVariants,
  photoVariants2,
  photoVariants3,
} from "../../variants/variants";

import { Navigation, Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import LinkComponent from "../LinkComponent";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const ProductDetail = () => {
  const { id1, id2 } = useParams();
  const { t } = useTranslation();
  const category = DATA.find((category) => category.link === id1);
  const product = category.products.find((product) => product.link === id2);
  
  // Get translated product text
  const translatedText = t(`products.items.${id2}.text`, { defaultValue: '' });
  const productText = translatedText || product.text;
  
  const translatedTitle = t(`products.items.${id2}.title`, { defaultValue: '' });
  const productTitle = translatedTitle || product.title;

  // State to track the active slide index
  const [activeIndex, setActiveIndex] = useState(0);


  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);


  return (
    <div className="grid grid-cols-10">
      <div className="col-start-2 col-span-8 mb-28">
        <motion.div
          initial="hidden"
          whileInView="visible"
          exit="exit"
          variants={containerVariants}
          className=" w-full "
        >
          <LinkComponent />
          <motion.div className="text-30 py-2 flex">
            {(() => {
              const translatedCategory = t(`products.categories.${id1}`, { defaultValue: '' });
              
              // If translation exists, split it; otherwise use original
              let categoryParts = category.title;
              if (translatedCategory) {
                // Handle & symbol properly
                if (translatedCategory.includes('&')) {
                  categoryParts = translatedCategory.split('&').map(part => part.trim());
                } else {
                  categoryParts = translatedCategory.split(' ');
                }
              }
              
              return categoryParts.map((item, index) => (
                <React.Fragment key={index}>
                  <motion.h5
                    animate={{ translateY: 0, opacity: 1 }}
                    initial={{ translateY: -10, opacity: 0 }}
                    transition={{
                      duration: 1,
                      ease: "easeInOut",
                      delay: index * 0.2,
                    }}
                  >
                    {item}
                  </motion.h5>
                  &nbsp;
                </React.Fragment>
              ));
            })()}
          </motion.div>
        </motion.div>

        <motion.div className="w-full text-25 font-light flex justify-center items-center space-x-5 flex-wrap mt-7 mb-14">
          {category.products.map((item, index) => (
            <React.Fragment key={index}>
              <Link
                to={`/urunler/${category.link}/${item.link}`}
                className="flex flex-col group"
              >
                <motion.div
                  animate={{ translateY: 0, opacity: 1 }}
                  initial={{ translateY: -10, opacity: 0 }}
                  transition={{
                    duration: 1,
                    ease: "easeInOut",
                    delay: index * 0.2,
                  }}
              >
                {t(`products.items.${item.link}.title`, { defaultValue: '' }) || item.title}
              </motion.div>
                <motion.div
                  animate={{ translateY: 0, opacity: 1 }}
                  initial={{ translateY: -10, opacity: 0 }}
                  transition={{
                    duration: 1,
                    ease: "easeInOut",
                    delay: index * 0.2,
                  }}
                >
                  <div
                    className={`h-px w-[0%] bg-black ${
                      item.link === id2 && "w-[100%]"
                    } group-hover:!w-[100%]  transition-all`}
                  ></div>
                </motion.div>
              </Link>
              {index < category.products.length - 1 && (
                <motion.span 
                  className="text-customGray"
                  animate={{ translateY: 0, opacity: 1 }}
                  initial={{ translateY: -10, opacity: 0 }}
                  transition={{
                    duration: 1,
                    ease: "easeInOut",
                    delay: index * 0.2,
                  }}
                >
                  |
                </motion.span>
              )}
            </React.Fragment>
          ))}
        </motion.div>

        <div className="space-y-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            exit="exit"
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-10"
          >
            <motion.div className="h-full overflow-hidden">
              <Swiper
                modules={[Pagination, Autoplay]}
                pagination={{ clickable: true }}
                autoplay={{ delay: 2000 }}
                loop={true}
                onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                className="h-full overflow-hidden"
              >
                {product.images.map((image, index) => (
                  <SwiperSlide
                    className="h-full w-full overflow-hidden"
                    key={index}
                  >
                    <motion.img
                      initial="initial"
                      whileInView="whileInView"
                      exit="exit"
                      variants={photoVariants}
                      src={`/images/products/${id1}/${id2}/${image}.jpg`}
                      className="w-full h-full object-cover"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </motion.div>

            <div className="md:aspect-square flex flex-col justify-between">
  <div className="flex flex-col mb-10">
    <motion.h2
      variants={itemVariants}
      className="text-65 mb-5 text-customYellow"
    >
      {productTitle}
    </motion.h2>
    <motion.p
      variants={itemVariants}
      className="font-light text-customGray flex flex-col"
    >
      {Array.isArray(productText)
        ? productText.map((item, index) => (
            <span key={index}>{item}</span> 
          ))
        : productText} 
    </motion.p>
  </div>
  {/* <div className="text-customGray">
                <motion.h6 variants={itemVariants}>{t("products.productContent")}</motion.h6>
    {product.detail.map((item, index) => (
      <motion.h6 variants={itemVariants} key={index}>
        {item}
      </motion.h6>
    ))}
  </div> */}
</div>

          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div
             
              className="col-span-1 md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-2"
            >
              {product.images.map((item, index) => (
                <motion.div  initial="hidden"
                whileInView="visible"
                exit="exit"
                variants={containerVariants} className="overflow-hidden">
                  <motion.div
                    animate={
                      index === activeIndex ? { scale: 1.05 } : { scale: 1 }
                    }
                  
                    className="overflow-hidden"
                    onClick={() => {
                      setLightboxOpen(true);
                      setLightboxIndex(index);
                    }}
                  >
                    <div className="group cursor-pointer">
                      <motion.div
                        key={index}
                        className="aspect-square  overflow-hidden relative"
                      >
                        <AnimatePresence>
                          {index === activeIndex && (
                            <motion.div
                              animate={{ scale: 1 }}
                              initial={{ scale: 0 }}
                              transition={{ duration: 1 }}
                              exit={{ scale: 0 }}
                              className="absolute z-50 top-4 left-4"
                            >
                              <div className=" h-5 w-5  bg-black rounded-full  group-hover:scale-125 transition-all duration-300 group-hover:opacity-50"></div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div className="h-full w-full group-hover:scale-125 transition-all duration-300 ease-in-out">
                          <motion.img
                            initial="initial"
                            whileInView="whileInView"
                            exit="exit"
                            variants={photoVariants3}
                            src={`/images/products/${id1}/${id2}/${item}.jpg`}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-80 duration-300 ">
                          <img
                            src="/images/icons/magnifying-glass.svg"
                            className=" h-7 w-7"
                          />
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>






          
        </div>


              
                {lightboxOpen && (
          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            slides={product.images.map((img) => ({
              src: `/images/products/${id1}/${id2}/${img}.jpg`,
            }))}
            index={lightboxIndex}
            onIndexChange={setLightboxIndex}
          />
        )}















        <motion.div
          initial="hidden"
          whileInView="visible"
          exit="exit"
          variants={containerVariants}
          className=" flex flex-col mt-20 space-y-2"
        >
          <motion.h6 className="mb-2" variants={itemVariants}>
            {t("products.otherCategories")}
          </motion.h6>

          {DATA.map((item, index) => {
            const translatedCategory = t(`products.categories.${item.link}`, { defaultValue: '' });
            
            // If translation exists, split it; otherwise use original
            let categoryParts = item.title;
            if (translatedCategory) {
              // Handle & symbol properly
              if (translatedCategory.includes('&')) {
                categoryParts = translatedCategory.split('&').map(part => part.trim());
              } else {
                categoryParts = translatedCategory.split(' ');
              }
            }
            
            return (
              <Link
                key={index}
                to={`/urunler/${item.link}/${item.products[0].link}`}
                className="hover:translate-x-2 ease-in-out duration-300"
              >
                <motion.h6
                  variants={itemVariants}
                  className={`flex flex-col text-14 font-light ${
                    item.link === id1 && "font-normal"
                  }`}
                >
                  {categoryParts.map((item2, idx) => {
                    return (
                      <React.Fragment key={idx}>
                        {item2}
                        &nbsp;
                      </React.Fragment>
                    );
                  })}
                </motion.h6>
              </Link>
            );
          })}
        </motion.div>
















      </div>
    </div>
  );
};

export default ProductDetail;
